import { createFileRoute, Link } from "@tanstack/react-router";
import { Globe2, CheckCircle2 } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import type { CountryMeta } from "@/content/countries/meta.gen";
import { getActiveProvince } from "@/provinces/_registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { LongFormContent } from "@/components/LongFormContent";
import type { PageContent } from "@/content/site-pages";

export const Route = createFileRoute("/countries/")({
  component: CountriesHub,
  loader: async (): Promise<{
    hub: PageContent;
    countries: CountryMeta[];
    regions: string[];
  }> => {
    const [{ HUB_COUNTRIES_CONTENT }, { COUNTRY_META, COUNTRY_REGIONS_META }] =
      await Promise.all([
        import("@/content/longform-hubs"),
        import("@/content/countries/meta.gen"),
      ]);
    return {
      hub: HUB_COUNTRIES_CONTENT,
      countries: COUNTRY_META,
      regions: [...COUNTRY_REGIONS_META],
    };
  },
  head: ({ loaderData }) => {
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `เอกสาร & วีซ่าแยกตามประเทศปลายทาง — Thai Notary ${config.nameTh}`;
    const description = `ไกด์เอกสาร Apostille, แปลรับรอง, จดทะเบียนสมรสและวีซ่า — UK, USA, Australia, Germany, Japan, Singapore, HK, Canada, Schengen และอีกหลายประเทศ จัดจาก${config.nameTh}`;
    return {
      ...pageHeadForProvince({ title, description, path: "/countries" }),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/countries")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ประเทศปลายทาง", url: `${site}/countries` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            url: `${site}/countries`,
            hasPart: (loaderData?.countries ?? []).map((c) => ({
              "@type": "WebPage",
              name: `${c.flag} ${c.nameTh}`,
              url: `${site}/countries/${c.slug}`,
              description: c.metaDesc,
            })),
          }),
        },
        ...(loaderData?.hub
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(buildFaqSchema(loaderData.hub.faqs)),
              },
            ]
          : []),
      ],
    };
  },
});

function CountriesHub() {
  const { config } = getActiveProvince();
  const { hub, countries, regions } = Route.useLoaderData() as {
    hub: PageContent;
    countries: CountryMeta[];
    regions: string[];
  };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-14 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <Globe2 className="h-3.5 w-3.5" /> Country Hubs
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">
              บริการแยกตามประเทศปลายทาง
            </h1>
            <p className="mx-auto mt-4 max-w-3xl text-base text-muted-foreground">
              เลือกประเทศที่คุณจะไป — เราเตรียม Apostille, แปลรับรอง, จดทะเบียนสมรส
              และวีซ่าให้ครบจากภูเก็ตในที่เดียว
            </p>
          </div>
        </section>

        {regions.map((region) => {
          const list = countries.filter((c) => c.region === region);
          if (list.length === 0) return null;
          return (
            <section key={region} className="border-b border-border/50">
              <div className="mx-auto max-w-5xl px-4 py-10">
                <h2 className="mb-6 text-xl font-bold text-foreground md:text-2xl">
                  {region}
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {list.map((c) => (
                    <Link
                      key={c.slug}
                      to="/countries/$country"
                      params={{ country: c.slug }}
                      className="group flex flex-col rounded-xl border border-border bg-card p-5 transition hover:border-primary hover:shadow-md"
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-3xl leading-none">{c.flag}</span>
                        <div>
                          <h3 className="font-bold text-foreground group-hover:text-primary">
                            {c.nameTh}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {c.nameEn}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                        {c.isApostille && (
                          <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-semibold text-emerald-600">
                            <CheckCircle2 className="mr-0.5 inline h-3 w-3" /> Apostille
                          </span>
                        )}
                        <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                          แปล {c.language}
                        </span>
                      </div>
                      <p className="mt-3 line-clamp-2 text-xs text-muted-foreground">
                        {c.intro}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        <section className="bg-muted/30 py-12">
          <div className="mx-auto max-w-3xl px-4 text-center">
            <p className="text-sm text-muted-foreground">
              ไม่เจอประเทศที่คุณต้องการ? ทีม{config.nameTh}ของเราดูแลเอกสารไป
              <strong> ทุกประเทศทั่วโลก</strong> — โทรปรึกษาได้เลย
            </p>
          </div>
        </section>
        <LongFormContent content={hub} />
      </main>
      <SiteFooter />
    </div>
  );
}